/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { DirectionalHint } from '@fluentui/react/lib/Callout';
import { TeachingBubble } from '@fluentui/react/lib/TeachingBubble';
import { Toggle } from '@fluentui/react/lib/Toggle';
import * as React from 'react';
import { PanelRegion, PanelType, ScenarioName, StatusName, StepName, SubWorkLoad } from '../../../logger';
import { RequestPageVersion } from '../../../service/gen';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { updateUserSettings, useUserSettings } from '../../../utilities/UserSettings/UserSettingsUtils';
import { CreateRequest } from '../CreateRequest/CreateRequest';
import { CreateRequestProps } from '../CreateRequest/CreateRequest.types';
import { CreateRequestV2, CreateRequestV2Props } from '../CreateRequestV2/CreateRequestV2';
import { LoadingPage } from '../LoadingPage';
import { getTeachingBubbleStyles } from './CreateRequestSwitcher.styles';

export type CreateRequestSwitcherProps = CreateRequestProps & CreateRequestV2Props;

export const CreateRequestSwitcher: React.FC<CreateRequestSwitcherProps> = (
    props: CreateRequestSwitcherProps
): JSX.Element => {
    const host = getAppHost();
    const loc = host.getLocale();
    const logger = host.getLogger();

    const userSettings = useUserSettings();
    const [hideCoachMarkOnce, setHideCoachMarkOnce] = React.useState(false);

    if (userSettings === null) return <LoadingPage />;

    const logToggleButton = (requestPageVersion: RequestPageVersion): void => {
        logger.markScenario(ScenarioName.CreateApprovals, StepName.ChangeRequestPageVersion, StatusName.NA, {
            data: { requestPageVersion },
        });
        logger.logUserBiAction(
            SubWorkLoad.AppInteraction,
            ScenarioName.CreateApprovals,
            StepName.ChangeRequestPageVersion,
            'click',
            'nav',
            'CreateRequestSwitcher',
            'ToggleButton',
            PanelRegion.Modal,
            PanelType.CreateApproval,
            requestPageVersion
        );
    };

    const logCoachMark = (stepName: StepName): void => {
        logger.markScenario(ScenarioName.CreateApprovals, stepName, StatusName.NA);
        logger.logUserBiAction(
            SubWorkLoad.AppInteraction,
            ScenarioName.CreateApprovals,
            stepName,
            'click',
            'nav',
            'CreateRequestSwitcher',
            'CoachMark',
            PanelRegion.Modal,
            PanelType.CreateApproval
        );
    };

    const showNewRequestPage = userSettings.RequestPageVersion === RequestPageVersion.V2_0;
    const onToggleCheckedChange = (_: React.MouseEvent<HTMLElement, MouseEvent>, checked: boolean): void => {
        const requestPageVersion = checked ? RequestPageVersion.V2_0 : RequestPageVersion.V1_0;
        logToggleButton(requestPageVersion);
        updateUserSettings({
            RequestPageVersion: requestPageVersion,
            RequestPageHideCoachmark: true,
        });
    };

    const showCoachmark = !showNewRequestPage && !hideCoachMarkOnce && !userSettings.RequestPageHideCoachmark;
    const onCoachmarkDismiss = (): void => setHideCoachMarkOnce(true);
    const onCoachmarkPrimaryButtonClick = (ev: React.MouseEvent<HTMLElement, MouseEvent>): void => {
        logCoachMark(StepName.TryItNewRequestPageCoachMark);
        onToggleCheckedChange(ev, true);
    };
    const onCoachmarkSecondaryButtonClick = (): void => {
        logCoachMark(StepName.GotItNewRequestPageCoachMark);
        updateUserSettings({ RequestPageHideCoachmark: true });
    };
    const toggle = (
        <>
            <Toggle
                id='create-request-toggle-button'
                onText={loc.strings.new_request_page_toggle_on_label}
                offText={loc.strings.new_request_page_toggle_off_label}
                inlineLabel
                checked={showNewRequestPage}
                onChange={onToggleCheckedChange}
                role='switch'
            />
            {showCoachmark && (
                <TeachingBubble
                    calloutProps={{
                        calloutWidth: 280,
                        directionalHint: DirectionalHint.topAutoEdge,
                    }}
                    target='#create-request-toggle-button'
                    styles={getTeachingBubbleStyles}
                    headline={loc.strings.new_request_page_coachmark_title}
                    primaryButtonProps={{
                        children: loc.strings.new_request_page_coachmark_switch_button_title,
                        onClick: onCoachmarkPrimaryButtonClick,
                        ariaLabel: loc.strings.new_request_page_coachmark_switch_button_title,
                    }}
                    secondaryButtonProps={{
                        children: loc.strings.new_request_page_coachmark_dismiss_button_title,
                        onClick: onCoachmarkSecondaryButtonClick,
                        ariaLabel: loc.strings.new_request_page_coachmark_dismiss_button_title,
                    }}
                    hasCloseButton
                    onDismiss={onCoachmarkDismiss}>
                    {loc.strings.new_request_page_coachmark_content}
                </TeachingBubble>
            )}
        </>
    );

    const requestPage = showNewRequestPage ? (
        <CreateRequestV2 {...props} customFooterComponent={toggle} />
    ) : (
        <CreateRequest {...props} customFooterComponent={toggle} />
    );

    return requestPage;
};
